// Configuration Supabase
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = 'https://xjhktmconhidqpatxflw.supabase.co';
const supabaseAnonKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InhqaGt0bWNvbmhpZHFwYXR4Zmx3Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzQwMTc5OTgsImV4cCI6MjA4OTU5Mzk5OH0.yCv9MR-oEke-Y25k9UONEMRKQLe9lSMUY2tjOaqWfl8';

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Fonction pour créer un profil utilisateur dans Supabase
export const createUserProfile = async (user, username) => {
  try {
    // Utiliser le timestamp + email hash pour créer un UUID valide en attendant la correction de la table
    const userUUID = generateUUIDFromFirebaseUID(user.uid);
    
    const { data, error } = await supabase
      .from('users')
      .insert([
        {
          id: userUUID, // UUID généré à partir du Firebase UID
          email: user.email,
          username: username,
          role: 'viewer',
          created_at: new Date().toISOString(),
        }
      ]);

    if (error) throw error;
    
    // Stocker la correspondance Firebase UID -> UUID localement
    localStorage.setItem(`firebase_${user.uid}`, userUUID);
    
    return { data, error: null };
  } catch (error) {
    console.error('Erreur lors de la création du profil:', error);
    return { data: null, error };
  }
};

// Fonction pour générer un UUID valide à partir d'un Firebase UID
const generateUUIDFromFirebaseUID = (firebaseUID) => {
  // Créer un hash simple pour générer un UUID valide
  let hash = 0;
  for (let i = 0; i < firebaseUID.length; i++) {
    const char = firebaseUID.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  // Générer un UUID v4 basé sur le hash
  const hex = Math.abs(hash).toString(16).padStart(8, '0');
  const uuid = `${hex.slice(0, 8)}-${hex.slice(0, 4)}-4${hex.slice(1, 4)}-a${hex.slice(0, 3)}-${hex.padEnd(12, '0').slice(0, 12)}`;
  
  return uuid;
};

// Fonction pour récupérer l'UUID Supabase depuis le Firebase UID
const getSupabaseUUIDFromFirebaseUID = (firebaseUID) => {
  return localStorage.getItem(`firebase_${firebaseUID}`) || generateUUIDFromFirebaseUID(firebaseUID);
};

// Fonction pour récupérer un profil utilisateur
export const getUserProfile = async (userId) => {
  try {
    const supabaseUUID = getSupabaseUUIDFromFirebaseUID(userId);
    
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', supabaseUUID)
      .single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Erreur lors de la récupération du profil:', error);
    return { data: null, error };
  }
};

// Fonctions pour les vidéos
export const getAllVideos = async () => {
  try {
    const { data, error } = await supabase
      .from('videos')
      .select(`
        *,
        users (username, avatar_url),
        video_categories (
          categories (name)
        )
      `)
      .eq('status', 'published')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Erreur lors de la récupération des vidéos:', error);
    return { data: null, error };
  }
};

// Fonction pour récupérer une vidéo par ID
export const getVideoById = async (videoId) => {
  try {
    const { data, error } = await supabase
      .from('videos')
      .select(`
        *,
        users (username, avatar_url),
        video_categories (
          categories (name)
        )
      `)
      .eq('id', videoId)
      .single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Erreur lors de la récupération de la vidéo:', error);
    return { data: null, error };
  }
};

// Fonction pour récupérer les catégories
export const getCategories = async () => {
  try {
    const { data, error } = await supabase
      .from('categories')
      .select('*')
      .order('name');

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Erreur lors de la récupération des catégories:', error);
    return { data: null, error };
  }
};

// Fonction pour ajouter/supprimer une vidéo de la watchlist
export const toggleWatchlist = async (userId, videoId) => {
  try {
    // Vérifier si déjà dans la watchlist
    const { data: existing } = await supabase
      .from('watchlist')
      .select('id')
      .eq('user_id', userId)
      .eq('video_id', videoId)
      .single();

    if (existing) {
      // Supprimer de la watchlist
      const { error } = await supabase
        .from('watchlist')
        .delete()
        .eq('user_id', userId)
        .eq('video_id', videoId);

      if (error) throw error;
      return { added: false, error: null };
    } else {
      // Ajouter à la watchlist
      const { error } = await supabase
        .from('watchlist')
        .insert([{ user_id: userId, video_id: videoId }]);

      if (error) throw error;
      return { added: true, error: null };
    }
  } catch (error) {
    console.error('Erreur lors de la gestion de la watchlist:', error);
    return { added: null, error };
  }
};

// Fonction pour liker/unliker une vidéo
export const toggleLike = async (userId, videoId) => {
  try {
    // Vérifier si déjà liké
    const { data: existing } = await supabase
      .from('likes')
      .select('id')
      .eq('user_id', userId)
      .eq('video_id', videoId)
      .single();

    if (existing) {
      // Supprimer le like
      const { error } = await supabase
        .from('likes')
        .delete()
        .eq('user_id', userId)
        .eq('video_id', videoId);

      if (error) throw error;
      return { liked: false, error: null };
    } else {
      // Ajouter un like
      const { error } = await supabase
        .from('likes')
        .insert([{ user_id: userId, video_id: videoId }]);

      if (error) throw error;
      return { liked: true, error: null };
    }
  } catch (error) {
    console.error('Erreur lors de la gestion du like:', error);
    return { liked: null, error };
  }
};

// Fonction pour enregistrer une vue
export const recordView = async (userId, videoId) => {
  try {
    const { error } = await supabase
      .from('views')
      .insert([{ user_id: userId, video_id: videoId }]);

    if (error) throw error;
    return { error: null };
  } catch (error) {
    console.error('Erreur lors de l\'enregistrement de la vue:', error);
    return { error };
  }
};

// Fonction pour récupérer la watchlist d'un utilisateur
export const getUserWatchlist = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('watchlist')
      .select(`
        videos (
          *,
          users (username, avatar_url),
          video_categories (
            categories (name)
          )
        )
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return { data: data?.map(item => item.videos) || [], error: null };
  } catch (error) {
    console.error('Erreur lors de la récupération de la watchlist:', error);
    return { data: null, error };
  }
};